/**
 * Goal record schema — DataTable columns + FormModal fields for the
 * Goals half of Step 7 (`cybersecurity_goals_agent`, per ISO 21434
 * RQ-09-05). Goals are generated for "Reduce" treatment decisions
 * and feed Step 8 controls/requirements.
 *
 * Real DB shape:
 *   { goal_id (PK), goal_statement, risk_ids: string[],
 *     security_property ('Confidentiality'/'Integrity'/...),
 *     cal_level ('CAL1'/.../'CAL4'),
 *     // hierarchical: source, source_session_id, is_relevant,
 *     // relevance_justification, affected_components }
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import { getCALColor } from '../../../../utils/severityStyle';

export interface GoalRecord {
  goal_id: string;
  goal_statement?: string;
  risk_ids?: string[];
  security_property?: string;
  cal_level?: string;
  /** Hierarchical metadata when `source === 'child_tara'`. */
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

export const SECURITY_PROPERTIES = [
  { value: 'Confidentiality', label: 'Confidentiality' },
  { value: 'Integrity', label: 'Integrity' },
  { value: 'Availability', label: 'Availability' },
  { value: 'Authenticity', label: 'Authenticity' },
  { value: 'Non-repudiation', label: 'Non-repudiation' },
  { value: 'Authorization', label: 'Authorization' },
];

export const CAL_LEVELS = [
  { value: 'CAL1', label: 'CAL1' },
  { value: 'CAL2', label: 'CAL2' },
  { value: 'CAL3', label: 'CAL3' },
  { value: 'CAL4', label: 'CAL4' },
];

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

const renderProperty = (row: GoalRecord) =>
  row.security_property ? <CategoryPill label={String(row.security_property)} size="sm" /> : '';

const renderCal = (row: GoalRecord) =>
  row.cal_level ? (
    <LevelBadge
      color={getCALColor(row.cal_level)}
      label={String(row.cal_level)}
      size="sm"
    />
  ) : (
    ''
  );

/** Column widths sum to 100%. Edit mode reserves 4% for `_actions`. */
export const GOAL_COLUMNS_READ = [
  { key: 'goal_id', label: 'ID', sortable: true, width: '8%' },
  { key: 'goal_statement', label: 'Goal Statement', sortable: false, width: '48%' },
  {
    key: 'risk_ids',
    label: 'Linked Risks',
    sortable: false,
    width: '15%',
    render: (row: GoalRecord) => joinList(row.risk_ids),
  },
  { key: 'security_property', label: 'Property', sortable: true, width: '16%', render: renderProperty },
  { key: 'cal_level', label: 'CAL', sortable: true, width: '13%', render: renderCal },
];

export const GOAL_COLUMNS_EDIT = [
  { key: 'goal_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'goal_statement', label: 'Goal Statement', sortable: false, width: '46%' },
  {
    key: 'risk_ids',
    label: 'Linked Risks',
    sortable: false,
    width: '14%',
    render: (row: GoalRecord) => joinList(row.risk_ids),
  },
  { key: 'security_property', label: 'Property', sortable: true, width: '16%', render: renderProperty },
  { key: 'cal_level', label: 'CAL', sortable: true, width: '13%', render: renderCal },
];

export const GOAL_ACTIONS_COLUMN_WIDTH = '4%';

const GOAL_ID_PATTERN = /^[A-Z0-9_-]+$/;

/** FormModal field config. `risk_ids` options injected from the
 *  Step 6 risk list (multi-select; N:M to Step 6). */
export const GOAL_FORM_FIELDS = [
  {
    name: 'goal_id',
    label: 'Goal ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. CSG-01',
    helpText: 'Unique identifier (per ISO 21434 RQ-09-05).',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Goal ID is required';
      if (!GOAL_ID_PATTERN.test(s)) {
        return 'Goal ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'cal_level',
    label: 'CAL Level',
    type: 'select',
    required: true,
    options: CAL_LEVELS,
    helpText: 'Cybersecurity Assurance Level inherited from the risk assessment.',
    width: 'half',
  },
  {
    name: 'security_property',
    label: 'Security Property',
    type: 'select',
    required: true,
    options: SECURITY_PROPERTIES,
  },
  {
    name: 'risk_ids',
    label: 'Linked Risks',
    type: 'multi-select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'Risks this goal addresses (N:M relationship).',
    defaultValue: [],
  },
  {
    name: 'goal_statement',
    label: 'Goal Statement',
    type: 'textarea',
    required: true,
    rows: 4,
    placeholder: 'Define what security property to protect and why.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Goal statement must be at least 20 characters.';
      return null;
    },
  },
];
